import {toRaw} from "vue";
import useStore from "../store.ts";
import {postLayout, postRenderRequest, postUpdateGeometry} from "../worker/RenderWorkerManager.ts";
import {FontUrl, loadFonts} from "../utils/FontLoader.ts";
import {CacheManager} from "../cache/CacheManager.ts";
import type {CacheSnapshot} from "../cache/CacheSnapshot.ts";
import {combinedCloneHash} from "../cache/CacheSnapshot.ts";
import {Component} from "../types.ts";
import {ConnectionState} from "../socket/ConnectionState.ts";
import {processSubsInBatches} from "../utils/DatasheetUtils.ts";
import {parsePx} from "../utils/ElementUtils.ts";
import {
    chainStartRows,
    datasheetAuthoredStart,
    datasheetChannel,
    reportableRowCapacity,
    type ChainMember,
} from "../utils/PureElementUtils.ts";
import {RenderInvalidationReason, renderInvalidationRegistry} from "../utils/RenderInvalidation.ts";
import type {SocketCommandQueue} from "../socket/SocketCommandQueue.ts";
import {UpdateOrigin, type UpdateContextHandle} from "../utils/UpdateContextRegistry.ts";
import {orchestrator} from "../utils/Orchestrator.ts";

export interface LayoutResult {
    layout?: any[];
    font_urls?: FontUrl[];
    hash?: string;
    layout_id?: string;
    clone_transform?: CloneTransform;
    clone_transform_hash?: string;
    // see docs/EXTRACTED_COMMENTS.md#c082
    clone_transforms?: CloneTransform[];
}

/* see docs/EXTRACTED_COMMENTS.md#c083 */
export type LayoutApplySource = "cache" | "server" | "fallback";

export interface CloneTransform {
    base_layout_id: String;
    target_layout_id: String;
    channel_map: ChannelMapping[];
    // see docs/EXTRACTED_COMMENTS.md#c084
    container_id?: string;
}

export interface ChannelMapping {
    source: string;
    dest: string;
}

export interface SubPageEntry {
    start_row: number;
    channel: number
}

export interface LayoutChunkPayload {
    chunk_data: string;
    chunk_index: number;
    chunk_total: number;
}

/* see docs/EXTRACTED_COMMENTS.md#c085 */
interface AppliedLayoutRecord {
    effectiveId: string;
    hash: string | null;
    cloneHash: string;
    layout: any;
    fontUrls?: FontUrl[];
    cloneTransform?: CloneTransform;
    containerTransforms?: CloneTransform[];
}

export class LayoutManager {
    // see docs/EXTRACTED_COMMENTS.md#c086
    private layoutHashes = new Map<string, string>();
    private lastLayoutError: string | null = null;
    private lastLayoutErrorAt = 0;
    // see docs/EXTRACTED_COMMENTS.md#c087
    private lastPostedRowUsage: number | null = null;
    // see docs/EXTRACTED_COMMENTS.md#c088
    private clusterChannelTimer: ReturnType<typeof setTimeout> | null = null;
    private clusterChannelAttempts = 0;
    private rowUsageTimer: ReturnType<typeof setTimeout> | null = null;
    private rowUsageAttempts = 0;
    private static readonly CLUSTER_POLL_MAX_RETRIES = 60;
    private lastApplied: AppliedLayoutRecord | null = null;
    private subPageEntry: SubPageEntry | null = null;
    /* see docs/EXTRACTED_COMMENTS.md#c762 */
    private cachedRuleSeed: { layoutId: string; rules: Record<string, string> } | null = null;
    // see docs/EXTRACTED_COMMENTS.md#c089
    private rowStartTimer: ReturnType<typeof setTimeout> | null = null;
    private rowStartRunning = false;
    private rowStartPending = false;
    private static readonly ROW_START_DEFER_MS = 100;
    // see docs/EXTRACTED_COMMENTS.md#c667
    private supportedRows = new Map<string, number>();
    /* see docs/EXTRACTED_COMMENTS.md#c750 */
    private chainStarts = new Map<string, number>();
    private appliedChainStarts = new Map<string, number>();
    private recountDebounceTimer: ReturnType<typeof setTimeout> | null = null;
    private recountMaxWaitTimer: ReturnType<typeof setTimeout> | null = null;
    private recountConfirmTimer: ReturnType<typeof setTimeout> | null = null;
    private recountPending: number | null = null;
    private recountConfirmAttempts = 0;
    private static readonly RECOUNT_DEBOUNCE_MS = 400;
    private static readonly RECOUNT_MAX_WAIT_MS = 5000;
    private static readonly RECOUNT_CONFIRM_MS = 1500;
    private static readonly RECOUNT_CONFIRM_MAX_ATTEMPTS = 4;
    /* see docs/EXTRACTED_COMMENTS.md#c755 */
    private recountDeferrals = 0;
    private static readonly RECOUNT_DEFER_MAX_ATTEMPTS = 12;


    constructor(private readonly commandQueue: SocketCommandQueue) {}

    public async requestLayoutHash(): Promise<void> {
        this.requestLayout(true)
    }

    public setRowsUsed(rowsUsed: number, channel: number) {
        this.subPageEntry = {
            channel: channel,
            start_row: rowsUsed,
        }
    }

    public getRowsUsed() {
        return this.subPageEntry
    }

    public async requestLayout(hash_only?: boolean, layout_id = useStore().layout): Promise<void> {
        const store = useStore();
        if (!store.connected) return;
        if (store.error && store.error.includes("Maximum number of connections for")) return;
        if (layout_id == null || isNaN(layout_id as number)) return;
        console.log(`[LayoutManager] Requesting layout ${layout_id}.`);
        // see docs/EXTRACTED_COMMENTS.md#c398
        if (!hash_only) {
            this.commandQueue.send("request_layout", {
                "layout_id": layout_id,
            });
        } else {
            this.commandQueue.send("request_layout_hash", {
                "layout_id": layout_id,
            });
        }
        // see docs/EXTRACTED_COMMENTS.md#c399
    }

    // see docs/EXTRACTED_COMMENTS.md#c400
    private getLayoutSignature(effectiveId: string, hash?: string | null): string {
        return `${effectiveId}|${hash ?? ""}`;
    }

    private shouldSkipApply(effectiveId: string, hash?: string | null): boolean {
        if (this.lastApplied == null) return false;
        const signature = this.getLayoutSignature(effectiveId, hash);
        const appliedSignature = this.getLayoutSignature(this.lastApplied.effectiveId, this.lastApplied.hash);
        if (appliedSignature !== signature) return false;
        const store = useStore();
        if (String(store.layout ?? "") !== effectiveId) return false;
        // see docs/EXTRACTED_COMMENTS.md#c401
        if (toRaw(store.layoutData) !== this.lastApplied.layout) return false;
        return true;
    }

    public async requestLayoutBuild(_data: any): Promise<void> {
        console.log(`[LayoutManager] Requesting layout build.`);
        // this.socket.emit("request_build_layout", data);
    }

    public async handleLayoutResponse(result?: LayoutResult, error?: string): Promise<void> {
        const store = useStore();
        if (error) {
            await this.handleLayoutError(error);
            return;
        }

        if (result?.layout_id == null) {
            console.warn("[LayoutManager] dropping layout reply with no layout_id");
            return;
        }
        const layoutId = result.layout_id;
        const {layout, font_urls, clone_transform} = result || {layout: null, font_urls: null, clone_transform: undefined};
        const container_transforms = result?.clone_transforms;
        const incomingHash = result?.hash || null;
        // see docs/EXTRACTED_COMMENTS.md#c402
        const cloneHash = combinedCloneHash(result?.clone_transform_hash || null, container_transforms);

        if (layout && incomingHash) {
            console.log(`[LayoutManager] Received layout ${layoutId} from server`);
            this.layoutHashes.set(layoutId, incomingHash);

            // see docs/EXTRACTED_COMMENTS.md#c403
            if (layoutId == store.layout?.toString()) {
                await this.applyLayoutPayload(layout, font_urls, clone_transform, container_transforms, cloneHash ?? "", incomingHash);
            }
            return;
        }

        if (layoutId != store.layout?.toString()) return;

        if (layout && font_urls && !await this.hasLayoutCached(layoutId)) {
            await this.applyLayoutPayload(layout, font_urls, clone_transform, container_transforms, cloneHash || undefined, incomingHash);
        }else if (incomingHash) {
            // see docs/EXTRACTED_COMMENTS.md#c404
            const appliedCurrent = () =>
                this.lastApplied?.effectiveId === layoutId
                && this.lastApplied?.hash === incomingHash
                && (!cloneHash || this.lastApplied?.cloneHash === cloneHash)
                // Same identity check as shouldSkipApply: a record only counts
                // as applied while its payload is still the live layoutData.
                && toRaw(store.layoutData) === this.lastApplied?.layout;

            // see docs/EXTRACTED_COMMENTS.md#c409
            if (appliedCurrent()) {
                this.confirmServedLayoutForSubscriptions(store);
                return;
            }

            // see docs/EXTRACTED_COMMENTS.md#c757
            await this.maybeServeCachedLayout(layoutId, "server", incomingHash, cloneHash);
            if (appliedCurrent()) return;

            // see docs/EXTRACTED_COMMENTS.md#c405
            const takeover = cloneHash ? await CacheManager.instance.findSnapshotByHash(incomingHash) : null;
            if (takeover) {
                // see docs/EXTRACTED_COMMENTS.md#c406
                const takeoverContainers = result?.clone_transforms
                    ?? (takeover.clone_transforms as CloneTransform[] | undefined);
                const takeoverCloneHash = (result?.clone_transform_hash
                    ? cloneHash
                    : takeover.clone_transform_hash) ?? undefined;
                await this.applyLayoutPayload(
                    takeover.layout,
                    takeover.font_urls as FontUrl[] | undefined,
                    result?.clone_transform ?? takeover.clone_transform as CloneTransform | undefined,
                    takeoverContainers,
                    takeoverCloneHash,
                    incomingHash
                );
                // see docs/EXTRACTED_COMMENTS.md#c407
                if (appliedCurrent()) return;
            }

            // see docs/EXTRACTED_COMMENTS.md#c758
            console.log(`[LayoutManager] Layout ${layoutId} hash ${incomingHash} is not applied and not cached — requesting full layout.`);
            await this.requestLayout(false);
        }else {
            console.log(`[LayoutManager] Layout ${layoutId} is not valid in cache, requesting full layout.`);
            await this.requestLayout(false);
            return;
        }
    }

    public async handleAssignPage(page?: number, offline = false): Promise<void> {
        if (page == null) return;
        const store = useStore();
        console.log(`[LayoutManager] Assigning page ${page}.`);
        if (offline) {
            store.layout = Number(page);
            await this.maybeServeCachedLayout(page.toString());
            return;
        }
        if (Number(page) === store.layout) {
            // see docs/EXTRACTED_COMMENTS.md#c410
            await this.requestLayoutHash();
            return;
        }
        // see docs/EXTRACTED_COMMENTS.md#c411
        store.layout = Number(page);
    }

    public resetCalculatedRowFlags(ids?: string[]): void {
        const store = useStore();
        const targets = ids ?? store.datasheets ?? [];
        for (const id of targets) {
            const sheet = store.components[id];
            if (sheet?.flags) sheet.flags.calculated_row = false;
        }
    }

    private scheduleClusterChannelRetry(): void {
        if (this.clusterChannelTimer != null) return;
        if (++this.clusterChannelAttempts > LayoutManager.CLUSTER_POLL_MAX_RETRIES) {
            console.warn("[Cluster] handleClusterChannel abandoned after max retries");
            this.clusterChannelAttempts = 0;
            return;
        }
        this.clusterChannelTimer = setTimeout(() => {
            this.clusterChannelTimer = null;
            void this.handleClusterChannel();
        }, 5000);
    }

    private scheduleRowUsageRetry(amount?: number, force: boolean = true): void {
        if (this.rowUsageTimer != null) return;
        if (++this.rowUsageAttempts > LayoutManager.CLUSTER_POLL_MAX_RETRIES) {
            console.warn("[Cluster] handleRowUsage abandoned after max retries");
            this.rowUsageAttempts = 0;
            return;
        }
        this.rowUsageTimer = setTimeout(() => {
            this.rowUsageTimer = null;
            void this.handleRowUsage(amount, force);
        }, 5000);
    }

    /* see docs/EXTRACTED_COMMENTS.md#c667 */
    public scheduleRowUsageRecount(sheetId: string, supported: number): void {
        this.supportedRows.set(sheetId, supported);
        if (this.recountDebounceTimer != null) clearTimeout(this.recountDebounceTimer);
        this.recountDebounceTimer = setTimeout(
            () => this.fireRowUsageRecount(),
            LayoutManager.RECOUNT_DEBOUNCE_MS
        );
        if (this.recountMaxWaitTimer == null) {
            this.recountMaxWaitTimer = setTimeout(
                () => this.fireRowUsageRecount(),
                LayoutManager.RECOUNT_MAX_WAIT_MS
            );
        }
    }

    /** Layout reset: drop timers armed against the old sheet. Keeps supportedRows (#c766). */
    public cancelRowUsageRecount(): void {
        if (this.recountDebounceTimer != null) clearTimeout(this.recountDebounceTimer);
        if (this.recountMaxWaitTimer != null) clearTimeout(this.recountMaxWaitTimer);
        if (this.recountConfirmTimer != null) clearTimeout(this.recountConfirmTimer);
        this.recountDebounceTimer = null;
        this.recountMaxWaitTimer = null;
        this.recountConfirmTimer = null;
        this.recountPending = null;
        this.recountConfirmAttempts = 0;
        /* see docs/EXTRACTED_COMMENTS.md#c755 */
        this.recountDeferrals = 0;
        /* see docs/EXTRACTED_COMMENTS.md#c750 */
        /* see docs/EXTRACTED_COMMENTS.md#c766 */
        this.chainStarts.clear();
        this.appliedChainStarts.clear();
        // see docs/EXTRACTED_COMMENTS.md#c676
    }

    /** No reporting mid row-start rebuild, or before load. */
    private recountSuppressed(): boolean {
        const store = useStore();
        if (!store.loaded) return true;
        return this.rowStartPending || this.rowStartRunning || this.rowStartTimer != null;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c752 */
    private datasheetVisible(sheet: Component): boolean {
        const flags = sheet.published?.hide_flags;
        if (!flags) return true;
        return !(flags.rule || flags.anchoring || flags.render);
    }

    /** Measured when the renderer has spoken, else the honest-or-zero prediction (#c749). */
    private localSupported(sheet: Component): number {
        return this.supportedRows.get(sheet.props.id) || reportableRowCapacity(sheet);
    }

    private sheetComponents(): Component[] {
        const store = useStore();
        return (store.datasheets ?? [])
            .map((id) => store.components[id])
            .filter((s): s is Component => !!s);
    }

    /* see docs/EXTRACTED_COMMENTS.md#c695 */
    /* see docs/EXTRACTED_COMMENTS.md#c750 */
    private clusterChannel(): number {
        const sheets = this.sheetComponents();
        const wanted = useStore().cluster.channel;
        if (wanted && sheets.some((s) => datasheetChannel(s) === wanted)) return wanted;
        return (sheets[0] ? datasheetChannel(sheets[0]) : null) ?? 0;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c750 */
    private clusterChainSheets(): Component[] {
        const channel = this.clusterChannel();
        const sheets = this.sheetComponents();
        const onChannel = channel
            ? sheets.filter((s) => datasheetChannel(s) === channel)
            : sheets.slice(0, 1);
        const visible = onChannel.filter((s) => this.datasheetVisible(s));
        /* see docs/EXTRACTED_COMMENTS.md#c750 */
        const chain = visible.length > 0 ? visible : onChannel.slice(0, 1);
        return chain.sort((a, b) => (a.props.id < b.props.id ? -1 : a.props.id > b.props.id ? 1 : 0));
    }

    private collectChainMembers(): ChainMember[] {
        const store = useStore();
        const members: ChainMember[] = [];
        for (const id of store.datasheets ?? []) {
            const sheet = store.components[id];
            if (!sheet) continue;
            members.push({
                id,
                channel: datasheetChannel(sheet),
                visible: this.datasheetVisible(sheet),
                supported: this.localSupported(sheet),
            });
        }
        return members;
    }

    /** Recompute the chain offsets; true when they differ from what was last rebuilt. */
    private recomputeChainStarts(): boolean {
        const store = useStore();
        this.chainStarts = chainStartRows(
            this.collectChainMembers(),
            this.clusterChannel(),
            store.cluster.row_start,
        );
        return !sameStarts(this.chainStarts, this.appliedChainStarts);
    }

    /* see docs/EXTRACTED_COMMENTS.md#c750 */
    public startRowFor(sheetId: string): number {
        const start = this.chainStarts.get(sheetId);
        if (start != null) return start;
        const store = useStore();
        const sheet = store.components[sheetId];
        const channel = sheet ? datasheetChannel(sheet) : null;
        return channel != null && channel === this.clusterChannel() ? store.cluster.row_start : 0;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c764 */
    public rowBaseFor(sheetId: string): number {
        return datasheetAuthoredStart(useStore().components[sheetId]) ?? 0;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c750 */
    public reconcileChainStarts(): void {
        if (!useStore().loaded) return;
        if (!this.recomputeChainStarts()) return;
        void this.updateRowStart();
    }

    /* see docs/EXTRACTED_COMMENTS.md#c750 */
    private globalSupportedRows(): number | null {
        const chain = this.clusterChainSheets();
        if (chain.length === 0) return null;
        let total = 0;
        for (const sheet of chain) {
            if (!sheet.flags?.calculated_row) return null;
            const local = this.supportedRows.get(sheet.props.id);
            if (!local || local <= 0) return null;
            total += local;
        }
        return total;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c750 */
    private globalReportableRows(): number {
        const chain = this.clusterChainSheets();
        if (chain.length === 0) return 0;
        let total = 0;
        for (const sheet of chain) {
            const local = reportableRowCapacity(sheet);
            if (local <= 0) return 0;
            total += local;
        }
        return total;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c765 */
    /* see docs/EXTRACTED_COMMENTS.md#c766 */
    /* see docs/EXTRACTED_COMMENTS.md#c768 */
    private builtRows(sheet: Component): number {
        const rows = sheet.children ?? [];
        const shown = rows.filter((r) => !r.published?.hide_flags?.render).length;
        return shown > 0 ? shown : rows.length;
    }

    private localStrideRows(): number {
        const chain = this.clusterChainSheets();
        let total = 0;
        for (const sheet of chain) {
            const measured = this.supportedRows.get(sheet.props.id) ?? 0;
            if (measured > 0) {
                total += measured;
                continue;
            }
            const reportable = reportableRowCapacity(sheet);
            /* see docs/EXTRACTED_COMMENTS.md#c768 */
            total += reportable > 0 ? reportable : this.builtRows(sheet);
        }
        return total;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c750 */
    /* see docs/EXTRACTED_COMMENTS.md#c752 */
    /* see docs/EXTRACTED_COMMENTS.md#c765 */
    private refreshSubPageEntry(): void {
        const channel = this.clusterChannel();
        const stride = this.globalSupportedRows() ?? this.localStrideRows();
        /* see docs/EXTRACTED_COMMENTS.md#c755 */
        const known = this.subPageEntry;
        if (stride <= 0 && known && known.channel === channel && known.start_row > 0) return;
        if (!known || known.start_row !== stride || known.channel !== channel) {
            console.log(`[Cluster] page stride now ${stride} on channel ${channel}`);
        }
        this.setRowsUsed(stride, channel);
    }

    /* see docs/EXTRACTED_COMMENTS.md#c750 */
    public async onDatasheetBuilt(sheetId: string): Promise<void> {
        const store = useStore();
        const moved = this.recomputeChainStarts();
        this.refreshSubPageEntry();
        const sheet = store.components[sheetId];
        if (sheet?.flags.is_cluster_sheet || store.cluster.enabled) {
            await this.handleRowUsage(undefined, false);
        }
        if (moved) await this.updateRowStart();
    }

    /* see docs/EXTRACTED_COMMENTS.md#c750 */
    private observedSupportedRows(): number | null {
        return this.globalSupportedRows();
    }

    /* see docs/EXTRACTED_COMMENTS.md#c672 */
    private fireRowUsageRecount(): void {
        if (this.recountDebounceTimer != null) clearTimeout(this.recountDebounceTimer);
        if (this.recountMaxWaitTimer != null) clearTimeout(this.recountMaxWaitTimer);
        this.recountDebounceTimer = null;
        this.recountMaxWaitTimer = null;
        /* see docs/EXTRACTED_COMMENTS.md#c755 */
        if (this.recountSuppressed()
            && ++this.recountDeferrals <= LayoutManager.RECOUNT_DEFER_MAX_ATTEMPTS) {
            this.recountDebounceTimer = setTimeout(
                () => this.fireRowUsageRecount(),
                LayoutManager.RECOUNT_DEBOUNCE_MS
            );
            return;
        }
        this.recountDeferrals = 0;
        /* see docs/EXTRACTED_COMMENTS.md#c767 */
        this.refreshSubPageEntry();

        const count = this.observedSupportedRows();
        if (count == null || count <= 0) return;
        if (count === this.lastPostedRowUsage) {
            if (this.recountConfirmTimer != null) clearTimeout(this.recountConfirmTimer);
            this.recountConfirmTimer = null;
            this.recountPending = null;
            /* see docs/EXTRACTED_COMMENTS.md#c755 */
            this.reportRowUsage(count, false);
            return;
        }
        if (this.recountPending === count && this.recountConfirmTimer != null) return;
        this.recountPending = count;
        this.recountConfirmAttempts = 0;
        this.armRowUsageConfirm();
    }

    private armRowUsageConfirm(): void {
        if (this.recountConfirmTimer != null) clearTimeout(this.recountConfirmTimer);
        this.recountConfirmTimer = setTimeout(
            () => this.confirmRowUsageRecount(),
            LayoutManager.RECOUNT_CONFIRM_MS
        );
    }

    /* see docs/EXTRACTED_COMMENTS.md#c673 */
    private confirmRowUsageRecount(): void {
        this.recountConfirmTimer = null;
        const count = this.observedSupportedRows();
        if (count == null || count <= 0) {
            this.recountPending = null;
            return;
        }
        const attempts = ++this.recountConfirmAttempts;
        if (this.recountSuppressed() || count !== this.recountPending) {
            this.recountPending = count;
            if (attempts < LayoutManager.RECOUNT_CONFIRM_MAX_ATTEMPTS) {
                this.armRowUsageConfirm();
                return;
            }
            console.warn(`[Cluster] supported-row count unsettled after ${attempts} confirms — posting ${count}`);
        }
        this.recountPending = null;
        this.recountConfirmAttempts = 0;
        console.log(`[Cluster] supported rows settled at ${count} — reporting`);
        this.reportRowUsage(count, false);
    }

    /* see docs/EXTRACTED_COMMENTS.md#c412 */
    public reportRowUsage(count: number, force: boolean): void {
        /* see docs/EXTRACTED_COMMENTS.md#c749 */
        /* see docs/EXTRACTED_COMMENTS.md#c755 */
        if (this.subPageEntry) this.subPageEntry.start_row = count;
        if (!force && this.lastPostedRowUsage === count) return;
        this.lastPostedRowUsage = count;
        useStore().socketManager.postRowUsage(count);
    }

    public async handleClusterChannel(): Promise<void> {
        const store = useStore();
        const sheets = store.datasheets;
        if (!store.loaded) {
            this.scheduleClusterChannelRetry();
            return;
        }
        this.clusterChannelAttempts = 0;
        if (sheets.length == 0) return;
        const sheet = store.components[sheets[0]];
        if (!sheet) return;
        /* see docs/EXTRACTED_COMMENTS.md#c750 */
        await this.postClusterChannel(datasheetChannel(sheet) ?? 0);
    }

    public async handleRowUsage(amount?: number, force: boolean = true): Promise<void> {
        const store = useStore();
        const sheets = store.datasheets;
        if (!store.loaded) {
            this.scheduleRowUsageRetry(amount, force);
            return;
        }
        if (sheets.length == 0) {
            this.rowUsageAttempts = 0;
            return;
        }

        /* see docs/EXTRACTED_COMMENTS.md#c750 */
        const chain = this.clusterChainSheets();
        if (chain.length === 0 || chain.some((s) => !s.flags.calculated_row)) {
            this.scheduleRowUsageRetry(amount, force);
            return;
        }
        for (const member of chain) member.flags.is_cluster_sheet = true;
        // see docs/EXTRACTED_COMMENTS.md#c413
        /* see docs/EXTRACTED_COMMENTS.md#c674 */
        /* see docs/EXTRACTED_COMMENTS.md#c749 */
        /* see docs/EXTRACTED_COMMENTS.md#c750 */
        const capacity = amount ?? (this.globalSupportedRows() || this.globalReportableRows());
        if (capacity <= 0) {
            // see docs/EXTRACTED_COMMENTS.md#c414
            this.scheduleRowUsageRetry(amount, force);
            return;
        }
        this.rowUsageAttempts = 0;
        this.reportRowUsage(capacity, force);
    }

    /* see docs/EXTRACTED_COMMENTS.md#c415 */
    public async updateRowStart(): Promise<void> {
        this.rowStartPending = true;
        // see docs/EXTRACTED_COMMENTS.md#c416
        if (this.rowStartTimer != null) return;
        this.rowStartTimer = setTimeout(() => {
            this.rowStartTimer = null;
            void this.drainRowStart();
        }, LayoutManager.ROW_START_DEFER_MS);
    }

    /* see docs/EXTRACTED_COMMENTS.md#c417 */
    private async drainRowStart(): Promise<void> {
        if (this.rowStartRunning) return;
        this.rowStartRunning = true;
        try {
            while (this.rowStartPending) {
                this.rowStartPending = false;
                await this.applyRowStart();
            }
        } finally {
            this.rowStartRunning = false;
        }
    }

    private async applyRowStart(): Promise<void> {
        const store = useStore();
        if (!store.loaded) {
            console.warn(`[Cluster] applyRowStart skipped: store not loaded`);
            return;
        }
        /* see docs/EXTRACTED_COMMENTS.md#c695 */
        const resolved = this.clusterChannel();
        if (store.cluster.channel && store.cluster.channel !== resolved) {
            console.warn(
                `[Cluster] no sheet holds cluster.channel=${store.cluster.channel};`
                + ` offsetting channel ${resolved} instead`
            );
        }
        /* see docs/EXTRACTED_COMMENTS.md#c750 */
        this.recomputeChainStarts();
        /* see docs/EXTRACTED_COMMENTS.md#c752 */
        this.appliedChainStarts = new Map(this.chainStarts);
        const targets = (store.datasheets ?? [])
            .map((id) => store.components[id])
            .filter((s): s is Component => !!s && this.chainStarts.has(s.props.id));
        if (targets.length === 0) {
            console.warn(`[Cluster] applyRowStart skipped: no visible datasheet in the layout`);
            return;
        }
        /* see docs/EXTRACTED_COMMENTS.md#c751 */
        await orchestrator.enqueue(UpdateOrigin.LayoutRebuild, async (handle) => {
            for (const sheet of targets) {
                if (!sheet.flags.calculated_row) {
                    console.warn(`[Cluster] applyRowStart skipped: ${sheet.props.id} not built yet`);
                    continue;
                }
                const start = this.startRowFor(sheet.props.id);
                console.log(`[Cluster] Rebuilding datasheet ${sheet.props.id} with start row ${start}`);
                await this.rebuildDatasheet(sheet, start, handle);
            }
        });
    }

    /* see docs/EXTRACTED_COMMENTS.md#c751 */
    public async rebuildDatasheet(component: Component, start_idx: number, shared?: UpdateContextHandle) {
        const store = useStore();
        const sheet = store.components[component.props.id] ?? component;
        const sheetChannel = sheet.dynamic.key_channel ?? sheet.dynamic.channel;
        const children = sheet.children ?? [];
        if (children.length == 0) return;
        if (store.components[`${component.props.id}`].children.length <= 1) return;
        /* see docs/EXTRACTED_COMMENTS.md#c764 */
        start_idx += datasheetAuthoredStart(sheet) ?? 0;
        const parseTrailingNumber = (id: string): number => {
            const tail = id.split(".").pop() ?? "";
            const num = Number(tail.replace(/^0+/, ""));
            return Number.isFinite(num) ? num : NaN;
        };

        const first = parseTrailingNumber(store.components[`${component.props.id}`].children[0].props.id) - 1;

        // see docs/EXTRACTED_COMMENTS.md#c418
        const rowChildren = children.filter(c => {
            const t = parseTrailingNumber(c.props.id);
            return Number.isFinite(t) && t >= first + 1;
        });
        if (rowChildren.length > 0 && rowChildren.every(c => {
            const comp = store.components[c.props.id];
            return comp && comp.dynamic.row === start_idx + (parseTrailingNumber(c.props.id) - (first + 1));
        })) return;

        console.log("[LayoutManager] Rebuilding datasheet with start_idx", start_idx);

        const subsToRefresh = new Set<string>();

        const applyRowRecursive = (rootId: string, rowValue: number) => {
            const root = store.components[rootId];
            if (!root) return;


            const stack: Component[] = [root];
            while (stack.length) {
                const current = stack.pop()!;
                if (sheetChannel) {
                    const kc = current.dynamic?.key_channel ?? null;
                    const c  = current.dynamic?.channel ?? null;
                    if ((kc ?? c) === sheetChannel){
                        current.dynamic.row = rowValue;
                    }else if (current.dynamic.original_object.row == null) {
                        current.dynamic.row = null;
                    }
                }else {
                    current.dynamic.row = rowValue;
                }
                // see docs/EXTRACTED_COMMENTS.md#c419
                current.dynamic.found = false;
                current.adoptData = new Map();

                if (current.subscription) {
                    subsToRefresh.add(current.props.id);
                }

                const childs = current.children ?? [];
                for (let i = childs.length - 1; i >= 0; i--) {
                    const childId = childs[i].props.id;
                    stack.push(store.components[childId] ?? childs[i]);
                }
            }
        };

        const remap = async (handle: UpdateContextHandle) => {
            subsToRefresh.clear();
            const rebuiltRoots: string[] = [];
            for (const child of children) {
                const trailer = parseTrailingNumber(child.props.id);
                if (!Number.isFinite(trailer) || trailer < (first + 1)) continue;
                const targetRow = start_idx + (trailer - (first + 1));
                applyRowRecursive(child.props.id, targetRow);
                rebuiltRoots.push(child.props.id);
            }

            renderInvalidationRegistry.requestRender(
                RenderInvalidationReason.GeometryChanged,
                rebuiltRoots
            );

            processSubsInBatches(store, [...subsToRefresh], handle);
            await postUpdateGeometry(sheet.props.id, "datasheet-build");
            await postUpdateGeometry(component.props.id, "datasheet-build");
        };

        /* see docs/EXTRACTED_COMMENTS.md#c751 */
        if (shared) {
            await remap(shared);
            return;
        }
        await orchestrator.enqueue(UpdateOrigin.LayoutRebuild, remap);
    }

    public async postClusterChannel(channel: number): Promise<void> {
        const store = useStore();
        if (!store.connected) return;
        store.socketManager.postClusterChannel(channel);
    }

    public async handleBuildLayout(result?: LayoutResult): Promise<void> {
        if (!result) return;
        const {layout, font_urls} = result;
        const store = useStore();
        if (layout) {
            store.homeData = layout;
        }
        if (font_urls?.length) {
            await loadFonts(font_urls, store.device_settings!.content_url);
        }
    }

    /* see docs/EXTRACTED_COMMENTS.md#c420 */
    private async applyLayoutPayload(layout?: any[], fontUrls?: FontUrl[], clone_transform?: CloneTransform, container_transforms?: CloneTransform[], clone_transform_hash?: string, layoutHash?: string | null, source: LayoutApplySource = "server"): Promise<boolean> {
        CacheManager.instance.beginTransition(`applyLayoutPayload:${source}`);
        try {
            return await this.applyLayoutPayloadInner(layout, fontUrls, clone_transform, container_transforms, clone_transform_hash, layoutHash, source);
        } finally {
            CacheManager.instance.endTransition(`applyLayoutPayload:${source}`);
        }
    }

    private async applyLayoutPayloadInner(layout?: any[], fontUrls?: FontUrl[], clone_transform?: CloneTransform, container_transforms?: CloneTransform[], clone_transform_hash?: string, layoutHash?: string | null, source: LayoutApplySource = "server"): Promise<boolean> {
        const store = useStore();
        const layoutId = resultLayoutId(layout);
        const requested = store.layout != null ? String(store.layout) : null;
        const cloneTarget = clone_transform?.target_layout_id != null
            ? String(clone_transform.target_layout_id)
            : null;
        // see docs/EXTRACTED_COMMENTS.md#c421
        const effectiveId = cloneTarget ?? layoutId;
        if (!effectiveId || !requested || effectiveId !== requested) {
            console.log(`[LayoutManager] dropping apply (source=${source}) — payload ${effectiveId ?? "?"} (layout=${layoutId ?? "?"}, cloneTarget=${cloneTarget ?? "?"}) ≠ requested ${requested ?? "?"}`);
            store.layoutUnresolved = true;
            return false;
        }
        const effectiveHash = layoutHash ?? (layoutId ? this.layoutHashes.get(layoutId) ?? null : null);
        if (this.shouldSkipApply(effectiveId, effectiveHash)) {
            if (fontUrls && store.device_settings?.content_url)
            await loadFonts(fontUrls, store.device_settings.content_url);
            console.log(`[LayoutManager] applyLayout skipped (source=${source}, signature unchanged) — layout ${effectiveId}`);
            // see docs/EXTRACTED_COMMENTS.md#c422
            this.confirmServedLayoutForSubscriptions(store);
            return false;
        }
        console.log(`[LayoutManager] applyLayout (source=${source}) — layout ${layoutId ?? "?"} hash=${effectiveHash ?? "?"}`);
        // see docs/EXTRACTED_COMMENTS.md#c423
        store.socketManager?.socketListener?.resetInitialLoadState?.();
        store.fonts.urls = fontUrls || [];
        // First-load reveal gating resets per layout payload
        store.fonts.required.clear();
        for (const f of (store.fonts.urls || [])) {
            if (f?.name) store.fonts.required.add(f.name);
        }
        store.fonts.mainThreadLoaded.clear();
        store.fonts.workerReady.clear();
        if (store.device_settings?.content_url)
        await loadFonts(store.fonts.urls, store.device_settings.content_url);
        if (layout) {
            store.layoutUnresolved = false;
            store.layoutData = layout;
            const firstKey = Object.keys(layout)[0] as any | undefined;

            if (firstKey) {
                if (store.layoutData[firstKey]) {
                    const item = store.layoutData[firstKey];
                    store.ratio = parsePx(item?.style?.width ?? "") / parsePx(item?.style?.height ?? "");
                }
                const layoutId = firstKey.replace("_", "");
                if (clone_transform) {
                    store.clone.transform = JSON.parse(JSON.stringify(clone_transform));
                } else {
                    store.clone.transform  = {} as CloneTransform;
                }
                // see docs/EXTRACTED_COMMENTS.md#c424
                store.clone.containerTransforms = container_transforms?.length
                    ? JSON.parse(JSON.stringify(container_transforms))
                    : [];
                store.clone.transformHash = clone_transform_hash || "";
                postLayout(layoutId);
                postRenderRequest(["layoutLoaded"], [layoutId]);

            }
        }
        if (layoutId) {
            // see docs/EXTRACTED_COMMENTS.md#c425
            const switchingLayout = this.lastApplied?.effectiveId !== effectiveId;
            if (!store.cluster.enabled && switchingLayout) {
                this.subPageEntry = null;
                store.cluster.row_start = 0;
                store.cluster.channel = 0;
            }
            // see docs/EXTRACTED_COMMENTS.md#c426
            this.lastApplied = {
                effectiveId,
                hash: effectiveHash,
                cloneHash: clone_transform_hash || "",
                layout,
                fontUrls,
                cloneTransform: clone_transform,
                containerTransforms: container_transforms,
            };
        }
        if (fontUrls?.length) {
            const contentUrl = store.device_settings?.content_url;
            if (contentUrl) {
                await loadFonts(fontUrls, contentUrl);
            }
            // see docs/EXTRACTED_COMMENTS.md#c427
        }
        // see docs/EXTRACTED_COMMENTS.md#c428
        this.confirmServedLayoutForSubscriptions(store, !layout);
        return true;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c429 */
    private confirmServedLayoutForSubscriptions(
        store: ReturnType<typeof useStore>,
        flush: boolean = true,
    ): void {
        if (!store.connected) return;
        store.socketManager.confirmLayoutForSubscriptions();
        if (flush) {
            void store.channelManager.syncToServer().catch((error) => {
                console.warn("[LayoutManager] channel resync failed", error);
            });
        }
    }

    private async maybeServeCachedLayout(layoutId: string, source: LayoutApplySource = "server", expectedHash?: string | null, expectedCloneHash?: string | null): Promise<void> {
        try {
            const snapshot = await CacheManager.instance.restoreLatestSnapshot(layoutId);
            if (snapshot) {
                // see docs/EXTRACTED_COMMENTS.md#c757
                if (expectedHash && snapshot.hash !== expectedHash) return;
                if (expectedCloneHash && (snapshot.clone_transform_hash || "") !== expectedCloneHash) return;
                console.log(`[LayoutManager] Serving cached layout ${layoutId} (hash ${snapshot.hash}).`);
                // see docs/EXTRACTED_COMMENTS.md#c762
                this.cachedRuleSeed = {layoutId, rules: snapshot.rules ?? {}};
                const applied = await this.applyLayoutPayload(
                    snapshot.layout,
                    snapshot.font_urls as FontUrl[] | undefined,
                    snapshot.clone_transform as CloneTransform | undefined,
                    snapshot.clone_transforms as CloneTransform[] | undefined,
                    snapshot.clone_transform_hash,
                    snapshot.hash,
                    source
                );
                // see docs/EXTRACTED_COMMENTS.md#c430
                if (!applied) return;
                this.layoutHashes.set(layoutId, snapshot.hash);
                // see docs/EXTRACTED_COMMENTS.md#c431
                this.seedRulesFromSnapshot(snapshot);
            } else {
                console.warn(`Layout ${layoutId} not found in cache`);
            }
        } catch (error) {
            console.warn("[LayoutManager] Failed to restore cached snapshot", error);
        }
    }

    private seedRulesFromSnapshot(snapshot: CacheSnapshot): void {
        const store = useStore();
        try {
            store.ruleManager.seedFromSnapshot(snapshot.rules);
        } catch (e) {
            console.warn("[LayoutManager] rule seed failed", e);
        }
    }

    /* see docs/EXTRACTED_COMMENTS.md#c762 */
    public seedRulesForBuild(): void {
        const seed = this.cachedRuleSeed;
        if (!seed) return;
        const store = useStore();
        const requested = store.layout != null ? String(store.layout) : null;
        if (seed.layoutId !== requested) return;
        const count = Object.keys(seed.rules).length;
        if (count === 0) return;
        try {
            store.ruleManager.seedFromSnapshot(seed.rules);
            console.log(`[LayoutManager] Seeded ${count} cached rule(s) for layout ${seed.layoutId}.`);
        } catch (e) {
            console.warn("[LayoutManager] rule seed failed", e);
        }
    }

    /* see docs/EXTRACTED_COMMENTS.md#c432 */
    public composeSnapshotInput(): {
        layoutId: string;
        hash: string;
        cloneTransformHash?: string | null;
        layout: any;
        font_urls?: any[];
        clone_transform?: any;
        clone_transforms?: any[];
        channels: Record<string, any[]>;
        rules: Record<string, string>;
    } | null {
        const store = useStore();
        const requested = store.layout != null ? String(store.layout) : null;
        if (!requested) return null;
        const applied = this.lastApplied;
        if (!applied || applied.effectiveId !== requested) return null;
        if (!applied.hash || !applied.layout) return null;
        // see docs/EXTRACTED_COMMENTS.md#c433
        if (!store.loaded) return null;

        const channels: Record<string, any[]> = {};
        for (const ch of store.channelManager.getChannels()) {
            const rows = ch.getRows() as any[];
            channels[ch.getName()] = Array.isArray(rows) ? [...rows] : [];
        }
        const rules: Record<string, string> = {};
        for (const [name, rule] of store.ruleManager.getRules()) {
            const raw = (rule as any).getRawValue?.();
            if (typeof raw === "string") rules[name] = raw;
        }

        return {
            layoutId: requested,
            hash: applied.hash,
            cloneTransformHash: applied.cloneHash,
            layout: applied.layout,
            font_urls: applied.fontUrls?.length ? applied.fontUrls : undefined,
            clone_transform: applied.cloneTransform,
            clone_transforms: applied.containerTransforms,
            channels,
            rules,
        };
    }

    public async hasLayoutCached(layoutId: string): Promise<boolean> {
        try {
            const snap = await CacheManager.instance.restoreLatestSnapshot(layoutId);
            return snap != null;
        } catch (_e) {
            return false;
        }
    }

    /* see docs/EXTRACTED_COMMENTS.md#c434 */
    private async handleLayoutError(error: string): Promise<void> {
        const store = useStore();
        store.error = error;
        if (!error.includes("Please authenticate")) store.layoutUnresolved = true;
        console.log(error);
        store.connection.state = ConnectionState.Degraded;

        const now = Date.now();
        const sameError = this.lastLayoutError === error;
        const withinBackoff = now - this.lastLayoutErrorAt < 5000;
        this.lastLayoutError = error;
        this.lastLayoutErrorAt = now;

        if (sameError && withinBackoff) {
            return;
        }

        if (!store.connected) return;
        if (error.includes("Please authenticate")) {
            store.error = "";
            return;
        }

        try {
            console.warn("[LayoutManager] Layout error received, requesting full layout to resync.", error);
            await this.requestLayout(false);
        } catch (retryError) {
            console.warn("[LayoutManager] Retry after layout error failed", retryError);
        }
    }

}

function sameStarts(a: Map<string, number>, b: Map<string, number>): boolean {
    if (a.size !== b.size) return false;
    for (const [id, start] of a) {
        if (b.get(id) !== start) return false;
    }
    return true;
}

function resultLayoutId(layout?: any[]): string | null {
    if (!layout) return null;
    const firstKey = Object.keys(layout)[0];
    if (!firstKey) return null;
    return firstKey.replace("_", "");
}

